import * as Yup from 'yup';
import AlertComponent, { AlertType } from '@spa/components/alert/alert.component';
import FormGroupComponent, { InputType } from '@spa/components/form-group/form-group.component';
import FormikWithHoney, { WithHoneypot } from '@spa/components/forms/formik-with-honey/formikWithHoney.component';
import PopupComponent, { PopupContext, PopupType } from '@spa/components/popup/popup.component';
import React, { ReactElement, useState } from 'react';
import ButtonComponent from '@spa/components/button/button/button.component';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import { FormAction } from '@spa/lib/actions/newsletter/FormAction';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { UpdateOrUnsubscribeValues } from '@spa/lib/graphql/newsletter/sendUpdateOrUnsubscribeEmailV2';
import classNames from 'clsx';
import { createEmailSchema } from '@spa/lib/formsSchemas/commonValidations';
import { createPortal } from 'react-dom';
import sendUpdateOrUnsubscribeEmailAction from '@spa/lib/actions/newsletter/sendUpdateOrUnsubscribeEmailAction';
import styles from './newsletter-send-update-or-unsubscribe.component.module.scss';
import useGeneralSettings from '@spa/lib/react/hooks/useGeneralSettings';
import useHoneypotToken from '@spa/components/forms/formik-with-honey/useHoneypotToken';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
enum FormStatus {
  INITIAL = 'initial',
  SUCCESS = 'success',
  SUCCESS_AUTHENTICATED_USER = 'successAuthenticatedUser',
  FAILED = 'failed',
}
interface NewsletterUnsubscribeFormProps {
  readonly action: FormAction;
  readonly initialEmail: string;
  readonly hideRequiredAsterisk?: boolean;
  readonly isMbsNewsletter?: boolean;
  readonly isSessionLoading?: boolean;
}
const NewsletterSendUpdateOrUnsubscribeComponent = ({
  action,
  initialEmail,
  hideRequiredAsterisk,
  isMbsNewsletter = false,
  isSessionLoading = false
}: NewsletterUnsubscribeFormProps): ReactElement => {
  const [formStatus, setFormStatus] = React.useState<FormStatus>(FormStatus.INITIAL);
  const honeypotToken: string = useHoneypotToken();
  const {
    emarsysNewsletterEnabled
  } = useGeneralSettings();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const {
    formatMessage
  }: NamespacedIntl = useNamespacedIntl('newsletter');
  const {
    formatMessage: formsFormatMessage
  }: NamespacedIntl = useNamespacedIntl('forms');
  const {
    formatMessage: unsubscribeFormatMessage
  }: NamespacedIntl = useNamespacedIntl('newsletter-unsubscribe');
  const initialValues: UpdateOrUnsubscribeValues = {
    email: initialEmail
  };
  const handleSubmit = async (values: WithHoneypot<UpdateOrUnsubscribeValues>): Promise<void> => {
    try {
      const isTheSameEmailFromUser: boolean = initialEmail !== '' && initialEmail === values.email;
      const {
        status
      } = await sendUpdateOrUnsubscribeEmailAction('sendUpdateOrUnsubscribeEmailAction', {
        values,
        isAnonymous: !isTheSameEmailFromUser,
        honeypotToken,
        type: action,
        isMbsNewsletter
      });
      if (status === 'SUCCESS') {
        if (isTheSameEmailFromUser) {
          setFormStatus(FormStatus.SUCCESS_AUTHENTICATED_USER);
        } else {
          setFormStatus(FormStatus.SUCCESS);
        }
      } else {
        setFormStatus(FormStatus.FAILED);
      }
    } catch {
      setFormStatus(FormStatus.FAILED);
    }
    setShowPopup(false);
  };
  const validationSchema: Yup.Schema<UpdateOrUnsubscribeValues> = Yup.object().shape({
    email: createEmailSchema(formsFormatMessage)
  });
  return <div className={styles['newsletter-send']}>
      {showPopup && createPortal(<PopupComponent isOpen={showPopup} onClosed={(): void => setShowPopup(false)} popupContext={PopupContext.WISHLIST} popupType={PopupType.CENTER}>
            <FormikWithHoney initialValues={initialValues} honeypotToken={honeypotToken} onSubmit={values_0 => handleSubmit(values_0)}>
              {({
          isSubmitting
        }) => <div className={styles['newsletter-send__popup']}>
                  <ImageComponent src={`/assets/icons/delete-icon.svg`} alt={formatMessage('iconAlt')} width={96} height={96} />
                  <h3 className={classNames('bold')}>{formatMessage('popup.title')}</h3>
                  <p className={classNames('text-lg')}>{formatMessage('popup.subtitle')}</p>
                  <div className={styles['newsletter-send__popup-buttons']}>
                    <ButtonComponent buttonType={ButtonType.TERTIARY} text={formatMessage('popup.buttons.cancel')} disabled={isSubmitting} onClick={(): void => setShowPopup(false)} />
                    <ButtonComponent buttonType={ButtonType.PRIMARY} text={formatMessage('popup.buttons.proceed')} disabled={isSubmitting} showSpinner={isSubmitting} type={'submit'} />
                  </div>
                </div>}
            </FormikWithHoney>
          </PopupComponent>, document.body)}
      {formStatus === FormStatus.SUCCESS || formStatus === FormStatus.SUCCESS_AUTHENTICATED_USER || formStatus === FormStatus.FAILED ? <AlertComponent alertType={formStatus === FormStatus.SUCCESS || formStatus === FormStatus.SUCCESS_AUTHENTICATED_USER ? AlertType.SUCCESS : AlertType.ERROR} content={formStatus === FormStatus.SUCCESS_AUTHENTICATED_USER ? unsubscribeFormatMessage('success') : formatMessage(`sendForm.${action.toLowerCase()}.${formStatus}`)} /> : <FormikWithHoney<UpdateOrUnsubscribeValues> initialValues={initialValues} validationSchema={validationSchema} honeypotToken={honeypotToken} onSubmit={async (values_1, {
      setSubmitting
    }) => {
      if (isSessionLoading) {
        setSubmitting(false);
        return;
      }
      if (initialEmail !== '' && initialEmail === values_1.email) {
        setShowPopup(true);
      } else {
        await handleSubmit(values_1);
      }
      setSubmitting(false);
    }}>
          {({
        errors,
        touched,
        values: values_2,
        isSubmitting: isSubmitting_0,
        isValid
      }) => <div className={styles['newsletter-send__form']}>
              <div className={styles['newsletter-send__form-fields']}>
                <FormGroupComponent id="newsletter-send-update-or-unsubscribe" label={formsFormatMessage('fields.email')} name="email" touched={touched.email} error={errors.email} value={values_2.email} type={InputType.EMAIL} autoComplete="email" isDisabled={isSubmitting_0} required hideRequiredAsterisk={hideRequiredAsterisk ?? emarsysNewsletterEnabled} />
              </div>
              <div className={styles['newsletter-send__button']}>
                <ButtonComponent buttonType={ButtonType.SECONDARY} text={formsFormatMessage('buttons.basicSubmit')} type="submit" disabled={isSubmitting_0 || isSessionLoading} looksDisabled={isValid} />
              </div>
            </div>}
        </FormikWithHoney>}
    </div>;
};
export default NewsletterSendUpdateOrUnsubscribeComponent;